import { InnerBox } from "@/components/elements/InnerBox";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider, black, blue } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { Stack, Box, Typography, TextField, IconButton, Paper, CircularProgress } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import dayjs from "dayjs";
import { styled } from "@mui/system";
import { useState, useEffect, useRef } from "react";
import { typography } from "@/theme/typography";
import { useParams } from "react-router-dom";
import { createTalentCommentApi, useTalentCommentsList } from "@/api/services/main/talent";
import Cookies from "js-cookie";
import { MONTH_DATE_FORMAT_WITH_TIMES } from "@/constants/datetime";
import { TCreateTalentCommentResDto } from "@/api/services/main/talent/dtos/create-talent-comment.dto";
import { transformComments } from "@/features/talent/utils/convertTalentComment";
import { TTalentComment } from "@/api/entities/talent-comment.entity";
import { UiComment } from "@/features/talent/types/talentCommentForm";

type TTalentFormCommentProps = {
  defaultValues?: Partial<TTalentForm>;
};

interface CommentBubbleProps {
  isOwn?: boolean;
}

const CommentBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isOwn",
})<CommentBubbleProps>(({ isOwn }) => ({
  backgroundColor: isOwn ? blue[70] : black[99],
  color: isOwn ? "#fff" : black[30],
  padding: "8px",
  borderRadius: rounded.sm,
  width: "100%",
  fontFamily: "inherit",
  boxShadow: "none",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "pre-wrap",
  ...typography.body14Semi,
}));

export const TalentFormComment: FCC<TTalentFormCommentProps> = ({ defaultValues: _defaultValues }) => {
  const { talentId } = useParams();
  if (!talentId) return;

  const userId = Cookies.get("userId");
  const isIrohana = Cookies.get("isUser") === "false";

  const { data } = useTalentCommentsList(Number(talentId));

  const [comments, setComments] = useState<UiComment[]>([]);

  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (data?.data) {
      const commentList = transformComments(data.data as TTalentComment[], Number(userId));
      setComments(commentList);
    }
  }, [data, userId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments]);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const handleSend = async () => {
    if (!newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const { data: newTalentComment }: TCreateTalentCommentResDto = await createTalentCommentApi(Number(talentId), {
        commentText: newComment,
        userId: Number(userId),
      });

      setComments([
        ...comments,
        {
          name: newTalentComment.user?.fullName || "",
          text: newComment,
          time: dayjs(newTalentComment.createdAt).format(MONTH_DATE_FORMAT_WITH_TIMES),
          isOwn: true,
        },
      ]);
      setNewComment("");

      // Scroll to bottom with animation after state update
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="head20Bold" color={black[30]}>
            人材コメント
          </Typography>
        </Box>
      </Stack>

      <Stack
        ref={scrollRef}
        spacing={2}
        mt={3}
        sx={{
          maxHeight: 520,
          overflowY: "auto",
          scrollBehavior: "smooth",
        }}
      >
        {comments.map((comment, index) => (
          <Box key={index}>
            <Stack mb={0.5}>
              <Typography variant="body14Bold" color={black[70]} mb={0.5}>
                {comment.name}
              </Typography>
            </Stack>
            <CommentBubble elevation={0} isOwn={comment.isOwn}>
              {comment.text}
            </CommentBubble>
            <Typography variant="cap12" color={black[90]} mt={0.5}>
              {comment.time}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Box sx={{ borderTop: `1px solid ${divider.high}`, mt: 2, pt: 2, mx: -3 }}>
        <Stack direction="row" spacing={1} alignItems="center" px={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="入力してください"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !isSubmitting) {
                handleSend();
              }
            }}
            disabled={isSubmitting || isIrohana}
            inputProps={{
              maxLength: 1000,
            }}
            sx={{
              fontFamily: "inherit",
              backgroundColor: isSubmitting || isIrohana ? black[99] : "#FFF",
              borderRadius: rounded.sm,
              "& .MuiOutlinedInput-root": {
                borderRadius: rounded.sm,
                "&.Mui-focused fieldset": {
                  borderColor: blue[80],
                },
                "&.Mui-disabled": {
                  borderRadius: rounded.sm,
                  backgroundColor: black[99],
                  "& fieldset": {
                    borderRadius: rounded.sm,
                    borderColor: black[99],
                  },
                },
              },
              "& .MuiOutlinedInput-input.Mui-disabled": {
                color: black[70],
                borderRadius: rounded.sm,
                WebkitTextFillColor: black[70],
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={isSubmitting || !newComment.trim() || isIrohana}
            sx={{
              backgroundColor: isSubmitting || !newComment.trim() || isIrohana ? black[99] : "#FFF",
              color: isSubmitting || !newComment.trim() || isIrohana ? black[70] : blue[80],
              border: isSubmitting || !newComment.trim() || isIrohana ? "2px solid #e0e0e0" : `2px solid ${blue[80]}`,
              borderRadius: rounded.sm,
              padding: "7px 17px",
              "&:hover": {
                backgroundColor: isSubmitting || !newComment.trim() || isIrohana ? black[99] : "#f5f5f5",
              },
              "&.Mui-disabled": {
                backgroundColor: black[99],
                color: black[70],
                border: `2px solid ${black[99]}`,
                opacity: 1,
              },
            }}
          >
            {isSubmitting ? <CircularProgress size={24} sx={{ color: black[70] }} /> : <SendIcon />}
          </IconButton>
        </Stack>
      </Box>
    </InnerBox>
  );
};
